import { useEffect, useRef, useState, useMemo } from 'react'

import update from 'react-addons-update'

import { ModalsPlugin, GlobalModalTypeEnum } from '@api/local'
import EventBus from '@api/local/EventBus'
import {
  ACTIVATE_NO_PLAN_MESSAGE,
  logActivateBlockedMissingId,
  logActivateRequestSent,
  resolveActivateSubscriptionId,
} from '@client/lib/ActivateSubscriptionHelper'
import {
  RegisteredUserDetailsFragment, UserDetailsFragment, UserSubscriptionFragment, useActivateSubscription,
  UserCartDocument, UserDetailsDocument, useResetUserMenuMutation, useUpdateUserSubscription,
  useCheckoutCartMutation, NumberOfPortionsEnum,
} from '@hooks/api'
import { reportCheckoutUiStatusGap } from '@lib/checkoutObservability'
import { SiteHelper } from '@lib/SiteHelper'
import { FieldData } from '@molecules/index'
import { CheckOutFlowTypeEnum, CheckoutStepsEnum, UserCheckoutStatus } from '@uctypes/api/globalTypes'
import {
  getDbLastCheckoutStep, shouldLockSummaryPage, getStepIndexes, getRestrictedStepIndexes,
  shouldSyncSubscriptionFromApi, getHydratedSubscriptionValues, needsSubscriptionHydration, clampStepIndex,
  getStepName, getActionTitle, CheckoutFlowState, UseCheckoutFlowArgs, StepConfig, getOnDemandNextStep,
  getSubscriptionNextStep,
  getEffectiveShouldPresentCheckoutUI,
} from '@utility/CheckoutPure'
import * as checkoutStorage from '@utility/CheckoutStorage'
import { checkoutLogger } from '@utility/Logger'

// Main checkout flow hook
export function useCheckoutFlow({
  subscriptionSteps,
  onDemandSteps,
  plansMealKitCategories,
  userCartData,
  userDetailsData,
  addToast,
  form,
  checkoutStatus,
  shouldPresentCheckoutUI,
  initialCheckoutFlowType,
}: UseCheckoutFlowArgs) {

  const LOG_PREFIX = '[CHECKOUT_DEBUG]'
  const [updateSubscription] = useUpdateUserSubscription()
  const [activateSubscription] = useActivateSubscription()
  const [resetUserMenu] = useResetUserMenuMutation()
  const [checkoutCart] = useCheckoutCartMutation()

  // State for all local checkout flow data
  const [state, setState] = useState<CheckoutFlowState>({
    pageLoading: false,
    categoryId: null,
    currentCategoryId: null,
    numberOfMeals: null,
    numberOfPortions: null,
    selectedPlan: null,
    activeTab: '',
    currentStep: 0,
    maxCompleted: -1, // No steps marked complete initially
    checkoutFlowType: (initialCheckoutFlowType as CheckOutFlowTypeEnum) || checkoutStatus?.checkoutFlowType || CheckOutFlowTypeEnum.SUBSCRIPTION,
    hasConfirmedAddress: false,
    postConfirmLock: false, // Lock UI after confirm to prevent step regression
  })

  // Log every currentStep change
  useEffect(() => {
    checkoutLogger.info('[CHECKOUT][currentStep][CHANGE]', {
      currentStep: state.currentStep,
      maxCompleted: state.maxCompleted,
      state,
      stack: new Error().stack,
    })
  }, [state.currentStep])
  // const setStateWithLog: typeof setState = (...args: Parameters<typeof setState>) => {
  //   return setState(...args)
  // }
  const [isHydrated, setIsHydrated] = useState(false)

  const lastToastTime = useRef(0)
  const maxLocalStepRef = useRef(1) // Track max local step allowed for local progression
  // Prevent hydration override after signup navigation
  const postSignupNavLockRef = useRef(false)
  // Lock flow type if initialCheckoutFlowType is ON_DEMAND
  const flowTypeLocked = useRef(initialCheckoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND)
  const registeredUser = userDetailsData?.currentUser as UserDetailsFragment & RegisteredUserDetailsFragment
  const subscription: UserSubscriptionFragment = registeredUser?.subscriptions?.find((subscription) => subscription.id === userCartData?.currentUser?.activeMenu?.subscription?.id)
  const isGuestUser = userDetailsData?.currentUser?.__typename === 'GuestUser'
  const userId = userDetailsData?.currentUser?.id || checkoutStatus?.id

  // Track if user has interacted with stepper fields
  const hasUserInteracted = useRef(false)
  // True if we just hydrated from API and need to auto-advance
  const justHydratedFromApi = useRef(false)
  // Last completed step from API
  const dbLastCheckoutStep = getDbLastCheckoutStep(checkoutStatus)

  const currentSteps = useMemo(
    () =>
      state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION
        ? subscriptionSteps
        : onDemandSteps,
    [state.checkoutFlowType, subscriptionSteps, onDemandSteps],
  )

  // Loader state: Only show UI after checkoutStatus is loaded
  const isCheckoutStatusLoading = !checkoutStatus || typeof checkoutStatus.id === 'undefined'
  // Step readiness: true when hydrated and not loading
  const isStepReady = isHydrated && !isCheckoutStatusLoading

  // Only apply shouldPresentCheckoutUI-based lock for SUBSCRIPTION flow
  const effectiveShouldPresentCheckoutUI = getEffectiveShouldPresentCheckoutUI(
    state.checkoutFlowType,
    shouldPresentCheckoutUI,
    checkoutStatus,
  )
  // Track if summary step is locked (SUBSCRIPTION flow only)
  const hasLockedSummaryStep = useRef(false)
  hasLockedSummaryStep.current =
    state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION
      ? shouldLockSummaryPage(
        checkoutStatus,
        state.checkoutFlowType,
        hasLockedSummaryStep.current,
        effectiveShouldPresentCheckoutUI,
      )
      : false
  // If summary is locked, treat as UI not presentable
  const effectiveShouldPresentCheckoutUIOrLocked =
    state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION
      ? (hasLockedSummaryStep.current ? false : effectiveShouldPresentCheckoutUI)
      : true

  /**
   * Returns the step index to auto-advance to, or null if no auto-advance is needed.
   * Handles guest restrictions, step-by-step enforcement, and API-driven progression.
   */
  function getAutoAdvanceTargetStep(
    state: CheckoutFlowState,
    currentSteps: StepConfig[],
    flowType: CheckOutFlowTypeEnum | undefined,
    checkoutStatus: UserCheckoutStatus | undefined,
    isHydrated: boolean,
  ): number | null {
    let result: number | null = null

    // Log entry and key state for debugging auto-advance
    checkoutLogger.debug('[STEP_LOGIC][ENTER]', {
      currentStep: state.currentStep,
      flowType,
      isHydrated,
    })

    // Lock to summary step if shouldPresentCheckoutUI is false (SUBSCRIPTION only)
    if (
      flowType === CheckOutFlowTypeEnum.SUBSCRIPTION &&
      checkoutStatus && checkoutStatus?.shouldPresentCheckoutUI === false
    ) {
      checkoutLogger.debug('[STEP_LOGIC][LOCK_TO_SUMMARY]', { currentStep: state.currentStep })
      result = 0
      return result
    }

    if (state.postConfirmLock) {
      checkoutLogger.debug('[STEP_LOGIC][POST_CONFIRM_LOCK]', { currentStep: state.currentStep })
      return null
    }

    // Early returns for invalid state
    if (!currentSteps || !Array.isArray(currentSteps)) {
      checkoutLogger.debug('[STEP_LOGIC][NO_STEPS]', { })
      return null
    }
    if (!isHydrated || !checkoutStatus) {
      checkoutLogger.debug('[STEP_LOGIC][NOT_HYDRATED_OR_NO_STATUS]', { })
      return null
    }

    // Step indexes
    const { chooseAmountIdx, pickPlanIdx, addressStepIdx, paymentStepIdx, confirmDetailsIdx, lockedSummaryIdx } = getStepIndexes(currentSteps)
    // Log step indexes at entry for debugging
    checkoutLogger.debug('[STEP_LOGIC][STEP_INDEXES]', {
      chooseAmountIdx, pickPlanIdx, addressStepIdx, paymentStepIdx, confirmDetailsIdx, currentStep: state.currentStep,
    })

    // Guest users: only allow local progression between local steps
    if (isGuestUser) {
      const restrictedStepIndexes = getRestrictedStepIndexes({ addressStepIdx, paymentStepIdx, confirmDetailsIdx })
      if (restrictedStepIndexes.includes(state.currentStep)) {
        // Guest on restricted step, force back
        checkoutLogger.debug('[STEP_LOGIC][GUEST][RESTRICTED]', { currentStep: state.currentStep })
        return chooseAmountIdx
      }
      if (
        pickPlanIdx !== -1 &&
        state.numberOfPortions &&
        state.numberOfMeals &&
        !state.categoryId &&
        state.currentStep !== pickPlanIdx
      ) {
        checkoutLogger.debug('[STEP_LOGIC][GUEST][TO_PICK_PLAN]', { currentStep: state.currentStep })
        return pickPlanIdx
      }
      return null
    }

    // SUBSCRIPTION FLOW: enforce step-by-step progression via pure function
    if (flowType === CheckOutFlowTypeEnum.SUBSCRIPTION) {
      return getSubscriptionNextStep({
        currentStep: state.currentStep,
        chooseAmountIdx,
        pickPlanIdx,
        addressStepIdx,
        paymentStepIdx,
        confirmDetailsIdx,
        lockedSummaryIdx,
        state,
        checkoutStatus,
        checkoutLogger,
      })
    }

    // ON_DEMAND FLOW: use pure function for step determination
    if (flowType === CheckOutFlowTypeEnum.ON_DEMAND) {
      const nextStep = getOnDemandNextStep({
        currentStep: state.currentStep,
        addressStepIdx,
        paymentStepIdx,
        confirmDetailsIdx,
        checkoutStatus,
      })
      if (nextStep !== null && nextStep !== state.currentStep) {
        checkoutLogger.debug('[STEP_LOGIC][ON_DEMAND][NEXT_STEP]', { currentStep: state.currentStep, nextStep })
      }
      return nextStep
    }
    return null
  }

  // --- Effect: After hydrating from API, auto-advance if needed ---
  useEffect(() => {
    if (
      justHydratedFromApi.current &&
      state.numberOfPortions &&
      state.numberOfMeals &&
      state.categoryId
    ) {
      const autoAdvanceTarget = getAutoAdvanceTargetStep(
        state,
        currentSteps,
        state.checkoutFlowType as CheckOutFlowTypeEnum,
        checkoutStatus,
        true,
      )
      if (autoAdvanceTarget !== null && state.currentStep !== autoAdvanceTarget) {
        checkoutLogger.info('[AUTO_ADVANCE][POST_HYDRATE][GUARDED]', { autoAdvanceTarget, currentStep: state.currentStep })
        setState(prev => {
          if (prev.currentStep === autoAdvanceTarget) {
            checkoutLogger.info('[AUTO_ADVANCE][POST_HYDRATE][SKIP_REDUNDANT]', { prevStep: prev.currentStep, autoAdvanceTarget })
            return prev
          }
          return { ...prev, currentStep: autoAdvanceTarget, maxCompleted: Math.max(prev.maxCompleted, autoAdvanceTarget) }
        })
      }
      justHydratedFromApi.current = false
    }
  }, [state.numberOfPortions, state.numberOfMeals, state.categoryId, currentSteps, checkoutStatus, state.currentStep, state.checkoutFlowType])

  useEffect(() => {
    // Minimal auto-advance effect log
    if (!effectiveShouldPresentCheckoutUIOrLocked) return
    if (!isHydrated) return
    if (state.postConfirmLock) {
      // Only keep lock if API still wants to show checkout UI
      if (
        checkoutStatus?.shouldPresentCheckoutUI === false ||
        checkoutStatus?.hasConfirmedCheckoutDetails ||
        checkoutStatus?.hasActiveSubscription
      ) {
        setState(prev => ({ ...prev, postConfirmLock: false }))
      } else {
        return
      }
    }
    // --- PATCH: Prevent auto-advance to address step in SUBSCRIPTION flow ---
    // Only allow auto-advance for non-subscription flows
    if (state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION) {
      // Do NOT auto-advance from PICK_PLAN to ADD_ADDRESS; require explicit Next button click
      // Allow auto-advance for other steps (e.g., after payment/confirm, if needed)
      const { chooseAmountIdx, pickPlanIdx } = getStepIndexes(currentSteps)
      // If user is on PICK_PLAN or earlier, do not auto-advance
      if (state.currentStep === chooseAmountIdx || state.currentStep === pickPlanIdx) {
        return
      }
    }
    const autoAdvanceTarget = getAutoAdvanceTargetStep(
      state,
      currentSteps,
      state.checkoutFlowType as CheckOutFlowTypeEnum,
      checkoutStatus,
      isHydrated,
    )
    if (autoAdvanceTarget !== null && state.currentStep !== autoAdvanceTarget) {
      checkoutLogger.info('[AUTO_ADVANCE][GUARDED]', { autoAdvanceTarget, currentStep: state.currentStep })
      setState(prev => {
        if (prev.currentStep === autoAdvanceTarget) {
          checkoutLogger.info('[AUTO_ADVANCE][SKIP_REDUNDANT]', { prevStep: prev.currentStep, autoAdvanceTarget })
          return prev
        }
        return { ...prev, currentStep: autoAdvanceTarget, maxCompleted: Math.max(prev.maxCompleted, autoAdvanceTarget) }
      })
    }
  }, [
    state,
    currentSteps,
    effectiveShouldPresentCheckoutUIOrLocked,
    state.checkoutFlowType,
    isHydrated,
    checkoutStatus,
    state.numberOfPortions,
    state.numberOfMeals,
    state.categoryId,
  ])

  // --- Hydration
  useEffect(() => {
    let fallbackTimeout: ReturnType<typeof setTimeout> | undefined
    if (postSignupNavLockRef.current) {
      // Skip hydration if lock is active
      return
    }
    // Minimal log: Hydration started
    checkoutLogger.debug('[HYDRATE][START]', { checkoutStatusId: checkoutStatus?.id, isHydrated, currentStep: state.currentStep })
    // If checkoutStatus is missing, set fallback timeout to hydrate after 2s
    if (!checkoutStatus || !checkoutStatus.id) {
      if (!isHydrated) {
        fallbackTimeout = setTimeout(() => {
          if (!isHydrated) {
            setIsHydrated(true)
            checkoutLogger.warn('[HYDRATE][FALLBACK_TIMEOUT]')
            reportCheckoutUiStatusGap({
              surface: 'useCheckoutFlow',
              reason: 'hydrate_fallback_timeout',
              sessionFlowType: initialCheckoutFlowType,
              stateFlowType: state.checkoutFlowType,
              hasCurrentUser: !!userCartData?.currentUser,
              hasCheckoutStatusId: false,
              isGuest: isGuestUser,
              isHydrated: false,
              isCheckoutStatusLoading: true,
            })
          }
        }, 2000)
      }
      return () => { if (fallbackTimeout) clearTimeout(fallbackTimeout) }
    }
    // --- Ensure isHydrated is set synchronously and only once ---
    if (!isHydrated) {
      setIsHydrated(true)
      checkoutLogger.debug('[HYDRATE][SET]', { isHydrated: true })
    }
    // --- PATCH: Always sync plan/portion/category from API/subscription if API says they are set but local state is missing ---
    if (shouldSyncSubscriptionFromApi(checkoutStatus, state, subscription)) {
      const { hydratedCategory, hydratedPeople, hydratedDishes } = getHydratedSubscriptionValues(subscription)
      // Minimal log: Sync from API if needed
      checkoutLogger.debug('[HYDRATE][SYNC_FROM_API]', { hydratedCategory, hydratedPeople, hydratedDishes })
      if (hydratedCategory && hydratedPeople && hydratedDishes && needsSubscriptionHydration(state, subscription)) {
        setState(prev => ({ ...prev, categoryId: hydratedCategory, numberOfPortions: hydratedPeople, numberOfMeals: hydratedDishes }))
        justHydratedFromApi.current = true
      }
    }

    // --- PATCH: Always set step via getAutoAdvanceTargetStep ---
    const autoAdvanceTarget = getAutoAdvanceTargetStep(
      state,
      currentSteps,
      state.checkoutFlowType as CheckOutFlowTypeEnum,
      checkoutStatus,
      true, // isHydrated is now true
    )
    if (autoAdvanceTarget !== null && state.currentStep !== autoAdvanceTarget) {
      checkoutLogger.debug('[HYDRATE][AUTO_ADVANCE]', { autoAdvanceTarget, currentStep: state.currentStep })
      setState(prev => ({ ...prev, currentStep: autoAdvanceTarget, maxCompleted: Math.max(prev.maxCompleted, autoAdvanceTarget) }))
    }
    // --- 1. API checkoutStatus is the single source of truth after login ---
    // (Removed verbose hydration state logs)
    // --- 2. Fallback: Use localStorage only if API checkoutStatus and subscription are unavailable ---
    if (!subscription && !checkoutStatus) {
      // Removed unused: const localProgress = checkoutLocalStorage.getCheckoutProgress(userId)
    }
    // If we reach here, hydration is complete
    setIsHydrated(true)
    // checkoutLogger.debug('[HYDRATE][COMPLETE]', { isHydrated: true, state })
    return () => { if (fallbackTimeout) clearTimeout(fallbackTimeout) }
  }, [userId, userDetailsData, currentSteps, checkoutStatus, form, state.checkoutFlowType, effectiveShouldPresentCheckoutUI, isHydrated, subscription, effectiveShouldPresentCheckoutUIOrLocked])

  // [CHECKOUT_DEBUG] Section: Persist to localStorage
  useEffect(() => {
    // --- PATCH: Prevent localStorage persistence if effectiveShouldPresentCheckoutUI is false ---
    if (checkoutStatus && effectiveShouldPresentCheckoutUI === false) {
      // Don't persist any state to localStorage if checkout UI should not be presented
      return
    }
    try {
      const { addressStepIdx } = getStepIndexes(currentSteps)
      if (state.currentStep < addressStepIdx || addressStepIdx === -1) {
        const toSave = {
          userId,
          flowType: state.checkoutFlowType,
          numberOfPortions: state.numberOfPortions,
          numberOfMeals: state.numberOfMeals,
          categoryId: state.categoryId,
          currentStep: state.currentStep,
          maxCompleted: state.maxCompleted,
        }
        checkoutStorage.setCheckoutProgress(userId, toSave)
      }
    } catch (err) {
      checkoutLogger.error('Persist: Failed to save checkout progress', { error: err, userId, state })
    }
  }, [state.currentStep, state.maxCompleted, state.numberOfPortions, state.numberOfMeals, state.categoryId, state.checkoutFlowType, userId, currentSteps, checkoutStatus, effectiveShouldPresentCheckoutUI])

  // Hydrate from subscription
  useEffect(() => {
    // Hydrate from subscription if present and not in checkout flow
    if (subscription && effectiveShouldPresentCheckoutUI === false) {
      const category = subscription.category.id
      const numberOfPeople = subscription.numberOfPortions
      const numberOfDishes = subscription.numberOfMeals
      const selectedPlan = plansMealKitCategories?.find((cat) => cat.id === category)
      setState((prevState) => update(prevState, {
        categoryId: { $set: category },
        numberOfPortions: { $set: numberOfPeople },
        numberOfMeals: { $set: numberOfDishes },
        selectedPlan: { $set: selectedPlan },
      }))
      form.setFieldsValue({
        peopleRadio: numberOfPeople,
        dishesRadio: numberOfDishes,
        categoryRadio: category,
      })
    }
  }, [userId, userDetailsData, currentSteps, dbLastCheckoutStep, form, effectiveShouldPresentCheckoutUI, subscription, plansMealKitCategories])

  // Sync checkoutFlowType with checkoutStatus.checkoutFlowType
  useEffect(() => {
    // Only update if not locked (i.e., user did not explicitly choose ON_DEMAND)
    if (!flowTypeLocked.current && checkoutStatus?.checkoutFlowType && state.checkoutFlowType !== checkoutStatus.checkoutFlowType) {
      setState(prev => ({ ...prev, checkoutFlowType: checkoutStatus.checkoutFlowType }))
    }
  }, [checkoutStatus?.checkoutFlowType])

  // Handlers
  const handleChange = (changedFields: FieldData[]) => {
    hasUserInteracted.current = true
    const now = Date.now()

    changedFields.forEach((field) => {
      const fieldName = field.name as string | string[]
      const name = Array.isArray(fieldName) ? fieldName[0] : fieldName
      if (name === 'peopleRadio') {
        setState((prevState) => {
          const updated = update(prevState, { numberOfPortions: { $set: field.value } })
          const { chooseAmountIdx, pickPlanIdx } = getStepIndexes(currentSteps)
          if (
            updated.currentStep === chooseAmountIdx &&
            updated.numberOfPortions &&
            updated.numberOfMeals &&
            pickPlanIdx !== -1
          ) {
            maxLocalStepRef.current = pickPlanIdx
            return { ...updated, currentStep: pickPlanIdx, maxCompleted: Math.max(updated.maxCompleted, pickPlanIdx) }
          }
          return updated
        })
        form.setFieldsValue({ peopleRadio: field.value })
        checkoutStorage.setCheckoutSession('sessionNumberOfPortions', String(field.value))
      }
      if (name === 'dishesRadio') {
        const peopleRadioValue = form.getFieldValue('peopleRadio')
        if (peopleRadioValue === undefined || peopleRadioValue === null) {
          if (now - lastToastTime.current > 1000) {
            addToast('Please select the number of people before choosing dishes.', {
              appearance: 'error',
              autoDismiss: true,
            })
            lastToastTime.current = now
          }
          return
        }
        setState((prevState) => {
          const updated = update(prevState, { numberOfMeals: { $set: field.value } })
          const { chooseAmountIdx, pickPlanIdx } = getStepIndexes(currentSteps)
          if (
            updated.currentStep === chooseAmountIdx &&
            updated.numberOfPortions &&
            updated.numberOfMeals &&
            pickPlanIdx !== -1
          ) {
            maxLocalStepRef.current = pickPlanIdx
            return { ...updated, currentStep: pickPlanIdx, maxCompleted: Math.max(updated.maxCompleted, pickPlanIdx) }
          }
          return updated
        })
        form.setFieldsValue({ dishesRadio: field.value })
        checkoutStorage.setCheckoutSession('sessionNumberOfMeals', String(field.value))
      }
      if (name === 'categoryRadio') {
        const { pickPlanIdx: pickAPlanStepIdx } = getStepIndexes(currentSteps)
        setState((prevState) => {
          let newMaxCompleted = prevState.maxCompleted
          if (prevState.currentStep <= pickAPlanStepIdx && prevState.maxCompleted < pickAPlanStepIdx) {
            newMaxCompleted = pickAPlanStepIdx
          }
          const updatedState = update(prevState, {
            categoryId: { $set: field.value },
            maxCompleted: { $set: newMaxCompleted },
          })
          return updatedState
        })
        form.setFieldsValue({ categoryRadio: field.value })
        checkoutStorage.setCheckoutSession('sessionCategoryId', String(field.value))
      }
    })
  }

  const handleUpdateSubscription = async (): Promise<void> => {
    // Do not call in ON_DEMAND flow
    if (state.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND) {
      return
    }
    try {
      setState(prevState => ({ ...prevState, pageLoading: true }))
      if (!userCartData?.currentUser?.activeMenu) {
        addToast('Could not find active menu', {
          appearance: 'error',
          autoDismiss: true,
        })
        return
      }

      const sessionNumberOfPortions = checkoutStorage.getCheckoutSession('sessionNumberOfPortions') as unknown as NumberOfPortionsEnum
      const sessionNumberOfMeals = checkoutStorage.getCheckoutSession('sessionNumberOfMeals')
      const sessionCategoryId = checkoutStorage.getCheckoutSession('sessionCategoryId')

      await updateSubscription({
        variables: {
          id: userCartData?.currentUser?.activeMenu?.subscription?.id,
          input: {
            category: sessionCategoryId || state.categoryId,
            numberOfMeals: Number(sessionNumberOfMeals) || state.numberOfMeals,
            numberOfPortions: sessionNumberOfPortions || state.numberOfPortions,
            updateMenu: true,
          },
        },
        // refetchQueries: [{ query: UserCartDocument }],
        // awaitRefetchQueries: true,
      })

      checkoutStorage.clearCheckoutSession('sessionNumberOfPortions')
      checkoutStorage.clearCheckoutSession('sessionNumberOfMeals')
      checkoutStorage.clearCheckoutSession('sessionCategoryId')

      const shouldActivateSubscription = !userCartData?.currentUser?.checkoutStatus?.hasActiveSubscription && userCartData?.currentUser?.checkoutStatus?.hasSetDeliveryAddressDetails && userCartData?.currentUser?.checkoutStatus?.hasSetPaymentMethod
      const shouldResetMenu = !isGuestUser && !userCartData?.currentUser?.checkoutStatus?.hasConfiguredBox

      if (shouldActivateSubscription) {
        const subscriptionIdForActivate = resolveActivateSubscriptionId({
          activeMenuSubscriptionId: userCartData?.currentUser?.activeMenu?.subscription?.id ?? undefined,
          subscriptionMatchId: subscription?.id ?? undefined,
          subscriptions: registeredUser?.subscriptions?.map((s) => ({ id: s.id, status: s.status })) ?? undefined,
        })
        if (!subscriptionIdForActivate) {
          logActivateBlockedMissingId('UseCheckoutFlow.handleUpdateSubscription')
          addToast(ACTIVATE_NO_PLAN_MESSAGE, { appearance: 'error', autoDismiss: true })
          setState(prevState => ({ ...prevState, pageLoading: false }))
          return
        }
        logActivateRequestSent('UseCheckoutFlow.handleUpdateSubscription', true)
        await activateSubscription({
          variables: {
            id: subscriptionIdForActivate,
            reset: !userCartData?.currentUser?.checkoutStatus?.hasConfiguredBox,
          },
          refetchQueries: [{
            query: UserCartDocument,
          }, {
            query: UserDetailsDocument,
          }],
          awaitRefetchQueries: true,
        })
        console.log('Subscription activated successfully!')
      } else if (shouldResetMenu) {
        await resetUserMenu({
          variables: {
            id: userCartData.currentUser.activeMenu.id,
          },
          refetchQueries: [{
            query: UserCartDocument,
          }],
          awaitRefetchQueries: true,
        })
        console.log('Menu reset successfully!')
      }

      setState(prevState => ({ ...prevState, pageLoading: false }))
    } catch (e) {
      setState(prevState => ({ ...prevState, pageLoading: false }))
      checkoutLogger.error('[SUBSCRIPTION][UPDATE_ERROR]', e)
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  // Dedicated handler for confirm details step
  const handleConfirmDetails = async (defaultCreditCardId: string, defaultAddressId: string) => {

    checkoutStorage.clearAllCheckoutProgress()

    // Only activate subscription if in SUBSCRIPTION flow and on CONFIRM_DETAILS step
    if (state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION) {
      try {
        checkoutLogger.info('[CHECKOUT_MODAL_DEBUG][SUBSCRIPTION] Entered handleConfirmDetails', { state })
        setState(prevState => ({ ...prevState, pageLoading: true, postConfirmLock: true })) // Lock UI to prevent step regression
        const subscriptionIdConfirm = resolveActivateSubscriptionId({
          activeMenuSubscriptionId: userCartData?.currentUser?.activeMenu?.subscription?.id ?? undefined,
          subscriptionMatchId: subscription?.id ?? undefined,
          subscriptions: registeredUser?.subscriptions?.map((s) => ({ id: s.id, status: s.status })) ?? undefined,
        })
        if (!subscriptionIdConfirm) {
          logActivateBlockedMissingId('UseCheckoutFlow.handleConfirmDetails')
          addToast(ACTIVATE_NO_PLAN_MESSAGE, { appearance: 'error', autoDismiss: true })
          setState(prevState => ({ ...prevState, pageLoading: false, postConfirmLock: false }))
          checkoutLogger.info('[CHECKOUT_MODAL_DEBUG][SUBSCRIPTION] No subscription ID resolved, aborting', { state })
          return
        }
        logActivateRequestSent('UseCheckoutFlow.handleConfirmDetails', true)
        await activateSubscription({
          variables: {
            id: subscriptionIdConfirm,
            reset: !userCartData?.currentUser?.checkoutStatus?.hasConfiguredBox,
          },
          refetchQueries: [{ query: UserCartDocument }, { query: UserDetailsDocument }],
          awaitRefetchQueries: true,
        })
        addToast('Subscription activated!', {
          appearance: 'success',
          autoDismiss: true,
        })
        checkoutLogger.info('[CHECKOUT_MODAL_DEBUG][SUBSCRIPTION] Subscription activated, setting showSuccessModal', { state })
        setState(prevState => ({ ...prevState, showSuccessModal: true, orderData: undefined, pageLoading: false }))
        // Robust local progression: if payment step just completed, move to confirm step immediately
        const { confirmDetailsIdx: confirmStepIndex } = getStepIndexes(currentSteps)
        if (state.currentStep !== confirmStepIndex && confirmStepIndex !== -1) {
          setState(prev => ({ ...prev, currentStep: confirmStepIndex, maxCompleted: Math.max(prev.maxCompleted, confirmStepIndex) }))
        }
      } catch (e) {
        addToast(e.message, {
          appearance: 'error',
          autoDismiss: true,
        })
        checkoutLogger.error('[CHECKOUT_MODAL_DEBUG][SUBSCRIPTION] Error in handleConfirmDetails', e)
        setState(prevState => ({ ...prevState, pageLoading: false, postConfirmLock: false })) // Unlock UI on error
      }
    } else {
      checkoutLogger.info('[ON_DEMAND][CONFIRM_ORDER]', 'Sending an on demand order')
      checkoutLogger.info('[CHECKOUT_MODAL_DEBUG][ON_DEMAND] Entered handleConfirmDetails', { state })
      setState(prevState => ({ ...prevState, pageLoading: true }))
      try {
        const response = await checkoutCart({
          variables: {
            id: userCartData.currentUser.activeCart.id,
            creditCardId: defaultCreditCardId,
            userAddressId: defaultAddressId,
          },
          refetchQueries: SiteHelper.getCheckoutRefetchQueries(),
          awaitRefetchQueries: true,
        })
        const orderData = response.data.checkoutCart.order
        checkoutLogger.info('[CHECKOUT_MODAL_DEBUG][ON_DEMAND] Order placed, setting showSuccessModal', { orderData, state })
        setState(prevState => ({ ...prevState, showSuccessModal: true, orderData, pageLoading: false }))
        // Robust local progression: if payment step just completed, move to confirm step immediately
        const { confirmDetailsIdx: confirmStepIndex } = getStepIndexes(currentSteps)
        if (state.currentStep !== confirmStepIndex && confirmStepIndex !== -1) {
          setState(prev => ({ ...prev, currentStep: confirmStepIndex, maxCompleted: Math.max(prev.maxCompleted, confirmStepIndex) }))
        }
        return orderData
      } catch (e) {
        setState(prevState => ({ ...prevState, pageLoading: false }))
        checkoutLogger.error('[CHECKOUT_MODAL_DEBUG][ON_DEMAND] Error in handleConfirmDetails', e)
        addToast(e.message, {
          appearance: 'error',
          autoDismiss: true,
        })
      }
    }
    // Always complete the step (legacy, for safety)
    const { confirmDetailsIdx: confirmStepIndex } = getStepIndexes(currentSteps)
    checkoutLogger.info('[CHECKOUT_MODAL_DEBUG] handleCompleteStep called', { confirmStepIndex, state })
    handleCompleteStep(confirmStepIndex)
  }

  // --- Final handler with merged [CHECKOUT_DEBUG] logs ---
  const handleGoToStep = (index: number): boolean => {
    // --- PATCH: Prevent guest users from advancing to address/payment/confirm steps ---
    const { addressStepIdx, paymentStepIdx, confirmDetailsIdx } = getStepIndexes(currentSteps)
    if (isGuestUser) {
      const restrictedStepIndexes = getRestrictedStepIndexes({ addressStepIdx, paymentStepIdx, confirmDetailsIdx })
      if (restrictedStepIndexes.includes(index)) {

        addToast('Please sign up or log in to continue to the next step.', {
          appearance: 'warning',
          autoDismiss: true,
        })
        // Open sign up modal
        ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.SIGN_UP)

        // Always keep user on current step (do not advance)

        checkoutLogger.info('[STEP_TRANSITION][BLOCKED_GUEST]', {
          prevStepIdx: state.currentStep,
          nextStepIdx: state.currentStep,
          maxCompleted: state.maxCompleted,
          stack: new Error().stack,
        })

        setState(prevState => ({ ...prevState, currentStep: state.currentStep }))
        return false
      }
    }
    // Clamp index to valid range
    const clampedIndex = clampStepIndex(index, currentSteps)

    checkoutLogger.info('[STEP_TRANSITION][GO_TO_STEP]', {
      prevStepIdx: state.currentStep,
      prevStepName: getStepName(state.currentStep, currentSteps, LOG_PREFIX),
      inProgressStepIdx: clampedIndex,
      inProgressStepName: getStepName(clampedIndex, currentSteps, LOG_PREFIX),
      upcomingStepIdx: clampedIndex + 1,
      upcomingStepName: getStepName(clampedIndex + 1, currentSteps, LOG_PREFIX),
      state,
      checkoutStatus,
      stack: new Error().stack,
    })

    setState(prevState => {
      checkoutLogger.debug('[STEP_TRANSITION][setStateWithCurrentStepLog]', {
        prevStepIdx: prevState.currentStep,
        nextStepIdx: clampedIndex,
        maxCompleted: prevState.maxCompleted,
        stack: new Error().stack,
      })
      return { ...prevState, currentStep: clampedIndex }
    })
    return true
  }
  const handleCompleteStep = (index: number) => {

    checkoutLogger.info('[STEP_COMPLETED]', {
      prevStepIdx: state.currentStep,
      prevStepName: getStepName(state.currentStep, currentSteps, LOG_PREFIX),
      completedStepIdx: index,
      completedStepName: getStepName(index, currentSteps, LOG_PREFIX),
      upcomingStepIdx: index + 1,
      upcomingStepName: getStepName(index + 1, currentSteps, LOG_PREFIX),
      state,
      checkoutStatus,
      stack: new Error().stack,
    })

    // Clamp nextStep to valid range
    const nextStep = Math.min(index + 1, currentSteps.length - 1)
    setState(prevState => {
      checkoutLogger.debug('[STEP_COMPLETED][setStateWithCurrentStepLog]', {
        prevStepIdx: prevState.currentStep,
        nextStepIdx: nextStep,
        prevMaxCompleted: prevState.maxCompleted,
        newMaxCompleted: Math.min(index, currentSteps.length - 1),
        stack: new Error().stack,
      })
      return { ...prevState, maxCompleted: Math.min(index, currentSteps.length - 1), currentStep: nextStep }
    })
    if (nextStep < currentSteps.length) {
      setState(prevState => {
        const newState = update(prevState, { currentStep: { $set: nextStep } })

        checkoutLogger.debug('[handleCompleteStep][setState] nextStep', {
          prevState,
          newState,
          prevStepIdx: index,
          prevStepName: getStepName(index, currentSteps, LOG_PREFIX),
          inProgressStepIdx: nextStep,
          inProgressStepName: getStepName(nextStep, currentSteps, LOG_PREFIX),
          upcomingStepIdx: nextStep + 1,
          upcomingStepName: getStepName(nextStep + 1, currentSteps, LOG_PREFIX),
          stack: new Error().stack,
        })

        return newState
      })
      if (typeof document !== 'undefined') {
        if (nextStep >= 3) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }

      checkoutLogger.info('Step transition', {
        prevStepIdx: index,
        prevStepName: getStepName(index, currentSteps, LOG_PREFIX),
        inProgressStepIdx: nextStep,
        inProgressStepName: getStepName(nextStep, currentSteps, LOG_PREFIX),
        upcomingStepIdx: nextStep + 1,
        upcomingStepName: getStepName(nextStep + 1, currentSteps, LOG_PREFIX),
        stack: new Error().stack,
      })

    }
  }

  // --- Patch handleResetFlow to log for ON_DEMAND ---
  const handleResetFlow = () => {
    if (state.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND) {

      checkoutLogger.info('[ON_DEMAND][handleResetFlow]', { state, stack: new Error().stack })

    }
    // Clear localStorage
    checkoutStorage.clearCheckoutProgress(userId)
    // Reset all in-memory state

    checkoutLogger.info('[RESET_FLOW][setState]', {
      prevState: state,
      nextState: {
        pageLoading: false,
        categoryId: null,
        currentCategoryId: null,
        numberOfMeals: null,
        numberOfPortions: null,
        selectedPlan: null,
        activeTab: '',
        currentStep: 0,
        maxCompleted: 0,
        checkoutFlowType: flowTypeLocked.current ? CheckOutFlowTypeEnum.ON_DEMAND : (checkoutStatus?.checkoutFlowType || CheckOutFlowTypeEnum.SUBSCRIPTION),
        hasConfirmedAddress: false,
      },
      stack: new Error().stack,
    })

    setState({
      pageLoading: false,
      categoryId: null,
      currentCategoryId: null,
      numberOfMeals: null,
      numberOfPortions: null,
      selectedPlan: null,
      activeTab: '',
      currentStep: 0,
      maxCompleted: 0,
      checkoutFlowType: flowTypeLocked.current ? CheckOutFlowTypeEnum.ON_DEMAND : (checkoutStatus?.checkoutFlowType || CheckOutFlowTypeEnum.SUBSCRIPTION),
      hasConfirmedAddress: false,
    })
    // Reset form fields
    form.resetFields(['peopleRadio', 'dishesRadio', 'categoryRadio'])
    // Clear any other memory (refs, timers, etc.)
    lastToastTime.current = 0
    // Optionally, clear any other module-level or singleton state here
    if (
      justHydratedFromApi.current &&
      state.numberOfPortions &&
      state.numberOfMeals &&
      state.categoryId
    ) {
      // Minimal log: Auto-advance after hydration
      const autoAdvanceTarget = getAutoAdvanceTargetStep(
        state,
        currentSteps,
        state.checkoutFlowType as CheckOutFlowTypeEnum,
        checkoutStatus,
        true,
      )
      if (autoAdvanceTarget !== null && state.currentStep !== autoAdvanceTarget) {
        checkoutLogger.debug('[AUTO_ADVANCE][POST_HYDRATE]', { autoAdvanceTarget, currentStep: state.currentStep })
        setState(prev => ({ ...prev, currentStep: autoAdvanceTarget, maxCompleted: Math.max(prev.maxCompleted, autoAdvanceTarget) }))
      }
      justHydratedFromApi.current = false
    }
  }

  // --- Guarded step handler: return status instead of calling UI ---
  function handleGoToStepGuarded(stepIndex: number) {
    // Block guest users from advancing to address step in BOTH flows
    const addressStepIdx = currentSteps.findIndex(s => s.id === CheckoutStepsEnum.ADD_ADDRESS)
    if (
      isGuestUser &&
      stepIndex === addressStepIdx
    ) {
      return { allowed: false, reason: 'GUEST_ON_DEMAND_BLOCK' }
    }
    const allowed = handleGoToStep(stepIndex)
    return { allowed }
  }

  // --- Listen for signup-success event and lock hydration ---
  useEffect(() => {
    // On signup-success, sync API (if needed) and let canonical progression logic handle step
    const handler = async () => {
      if (state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION) {
        // Sync API with latest plan/portion/category after signup
        await handleUpdateSubscription()
      }
      // No manual state override, let canonical logic (auto-advance effect) handle progression

      checkoutLogger.info(`${LOG_PREFIX}[SIGNUP_SUCCESS][API_SYNCED]`, { flow: state.checkoutFlowType })

    }
    EventBus.on('signup-success', handler)
    return () => EventBus.off('signup-success', handler)
  }, [currentSteps, state.checkoutFlowType, handleUpdateSubscription])

  // --- Compute actionTitle using pure function ---
  const actionTitle = getActionTitle({
    flowType: state.checkoutFlowType,
    currentStep: state.currentStep,
    currentSteps,
    checkoutStatus,
  })

  return {
    state,
    setState,
    handleChange,
    handleGoToStep,
    handleCompleteStep,
    handleResetFlow,
    handleUpdateSubscription,
    handleConfirmDetails,
    currentSteps,
    userId,
    isGuestUser,
    actionTitle,
    isCheckoutStatusLoading,
    isHydrated,
    isStepReady,
    handleGoToStepGuarded,
  }
}
